import { useCallback, useEffect, useMemo, useState } from "react";
import { Oval } from 'react-loader-spinner';
import { MainLayout } from "../components/MainLayout.tsx";
import MaintenanceScreen from "../components/MaintenanceScreen.tsx";
import { MainProvider } from "../context/MainContext.tsx";
import { useMergedConfig } from "../context/MergedConfigProvider.tsx";
import { PegaEventProvider } from "../context/PegaEventContext.tsx";
import { useMParticle } from "../hooks/useMparticle.ts";
import { useTranslations } from '../hooks/useTranslations.ts';
import { readDevPegaToken } from "../utils/devPegaToken.ts";
import type { PersistedPegaToken } from "../utils/persistedPegaToken.ts";
import { PegaJourney } from "./PegaJourney.tsx";
import { SignUpJourney } from "./SignUpJourney.tsx";

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === "true";

export const MainJourney = ({ journeyId }: { journeyId: string }) => {
  const { pegaStartingFields, embedOneTimeToken, accountId, iaid, multiasset, product } =
    useMergedConfig();
    const { isLoading } = useTranslations();
  const [oneTimeToken, setOneTimeToken] = useState<string | undefined>();
  const [connectApplicationId, setConnectApplicationId] = useState<
    string | undefined
  >();
  const [csrfToken, setCsrfToken] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [userPassword, setUserPassword] = useState<string | undefined>();
  const [persistedPegaToken, setPersistedPegaToken] = useState<
    PersistedPegaToken | undefined
  >();

  const { setUserData } = useMParticle();

  const onSignUpJourneyCompleted = useCallback(
    (
      oneTimeToken: string,
      signUpCsrfToken: string,
      _connectApplicationId?: string,
      email?: string,
      password?: string,
    ) => {
      setOneTimeToken(oneTimeToken);
      setCsrfToken(signUpCsrfToken);
      setConnectApplicationId(_connectApplicationId);
      if (email) {
        setUserData({ email });
        setUserEmail(email);
      }
      if (password) setUserPassword(password);
    },
    [setUserData],
  );

  useEffect(() => {
    // check if we already have a Pega token in localStorage
    // TODO: we no longer need to do this as we're not writing to localStorage
    // if (embedOneTimeToken) {
    //   console.log("clear stored Pega token due to embedOneTimeToken presence");
    //   deletePersistedPegaToken();
    //   return;
    // }
    // we no longer need to read from localStorage as we're not writing to it
    // const persistedPegaToken = readPersistedPegaToken();
    // if (persistedPegaToken) {
    //   setPersistedPegaToken(persistedPegaToken);
    // }
  }, [embedOneTimeToken]);

  // let the webview know the application has started once translations are loaded
  useEffect(() => {
    if (!isLoading) {
      window.postMessage({ applicationStarted: true }, "*");
    }
  }, [isLoading]);

  const onPegaTokenExpiry = useCallback(() => {
    setOneTimeToken(undefined);
    setPersistedPegaToken(undefined);
    // clearPersistedPegaToken(); // we no longer need to clear local storage as we're not writing to it
  }, []);
  // maybe move this to the config as well
  const startingFields = useMemo(
    () => ({
      ...pegaStartingFields,
      ConnectApplicationID: connectApplicationId,
      IAID: iaid,
      MultiAsset: multiasset,
      Product: product,
      AccountID: accountId,
    }),
    [connectApplicationId, pegaStartingFields, iaid, multiasset, product, accountId],
  );

  const devPegaToken = readDevPegaToken();
  const initiatedPegaJourney =
    oneTimeToken || embedOneTimeToken || devPegaToken || persistedPegaToken;

  if (MAINTENANCE_MODE) {
    return (
      <MainProvider journeyId={journeyId}>
        <MainLayout>
          <MaintenanceScreen
            message="This page is currently unavailable while we perform essential updates."
            estimatedTime="We expect to be back online in four hours. Stay in touch."
            showButton={true}
            buttonText="Close"
            buttonAction="close"
          />
        </MainLayout>
      </MainProvider>
    );
  }

  return (
    <MainProvider journeyId={journeyId}>


      <MainLayout>
      {isLoading ? <div className="flex justify-center items-center h-80">
        <Oval
          visible={true}
          height="100"
          width="100"
          color='#1B3876'
          secondaryColor='#FFF'
        />
      </div>
      :
        initiatedPegaJourney ? (
          <PegaEventProvider onPegaTokenExpiry={onPegaTokenExpiry}>
            <PegaJourney
              oneTimeToken={oneTimeToken}
              pegaTokenInfo={devPegaToken ?? persistedPegaToken?.token}
              pegaStartingFields={startingFields}
              embedOneTimeToken={embedOneTimeToken}
              iaid={iaid}
              csrfToken={csrfToken}
              userEmail={userEmail}
              userPassword={userPassword}
            />
          </PegaEventProvider>
        ) : (
          <SignUpJourney onCompleted={onSignUpJourneyCompleted} />
        )}
      </MainLayout>

    </MainProvider>
  );
};
